// THE FIREBASE SEAM: WHERE THE TWO TOKENS COME FROM, AND WHY A BUILD WITH NO VALUES STILL HAS NEITHER (W4.1, W4.2).
//
// `HttpTransport` needs two suppliers it cannot make itself — a Firebase Auth ID token (anonymous at launch,
// mirroring iOS) and a Firebase App Check token (reCAPTCHA Enterprise on web). This module is the one place either
// comes from, and it REFUSES, BY NAME, until the values behind them exist.
//
// ── NOTHING HERE IS FILLED IN, AND THE MISSING VALUES ARE NOT THIS BATCH'S TO INVENT ────────────────────────────────
//
// Every field of `FirebaseWebConfig` is a console's or Tyler's: the Firebase web app's `apiKey`, `authDomain`,
// `projectId` and `appId` come from registering a web app, and the reCAPTCHA Enterprise site key from creating a key.
// No web app is registered anywhere yet (`docs/WEB_READINESS.md`), and when one is it registers in `apealapp-staging`
// ONLY, until v1 verifies the App Check app id on the token (security sheet A-2; W2 design D14, still Tyler's).
// So this file names the variables and reads them; it holds no value for any of them, and neither does any other
// committed file. `web/.env.example` is the list, with nothing behind it.
//
// ── THIS FILE STILL HOLDS NO FIREBASE CODE, AND `firebaseSdk.ts` NOW DOES (W4.2-local) ──────────────────────────────
//
// W4.1 kept the seam type-only and argued three reasons, of which ONE HAS SINCE BEEN FALSIFIED and is recorded here
// rather than quietly deleted:
//
//   "THE SDK HALF CANNOT BE WRITTEN WITHOUT THE CONFIG."   FALSE, and W4.2-local is the counter-example. The Firebase
//   emulators include AUTH, so a local run supplies its own `apiKey`, `authDomain`, `projectId` and `appId` — words
//   the Auth emulator does not read, under a `demo-` project id no real Firebase project can have — and signs in
//   anonymously for real. `functions/test/v2-emulator/webClientEndToEnd.test.ts` drives every line of
//   `services/firebaseSdk.ts` against a running emulator, over real HTTP, into the real v2 handlers. What genuinely
//   needs a console is APP CHECK, which has no emulator at all, and the seam's answer to that is a NAMED FAILURE.
//
//   "A CLIENT WITH NO FIREBASE CODE IN IT CANNOT REACH FIREBASE."   Still the rule, in the strongest form left.
//   `firebase` is now a dependency, so the code exists; what preserves the property is that `main.tsx` reaches it
//   ONLY through an `await import(...)` taken after `readBaseUrl` has returned a reachable base URL. A build with no
//   `VITE_V2_BASE_URL` never loads a byte of it, and `vite build` puts it in its own chunk, which is a fact about the
//   build output rather than a promise. `web/test/guards.test.ts`'s no-URL-no-key rule is unchanged and still holds:
//   no origin, project id or key is written into this package.
//
//   "It would rewrite `web/package-lock.json` by thousands of lines."   True, and it did.
//
// THE PORT'S ONE IMPLEMENTATION IS `services/firebaseSdk.ts` — the start-up calls listed below, and the only file in
// this package that imports `firebase`. ACC-2 changed the fourth: until then every start signed in anonymously, which
// signs a real account out (F-AC-1), so start-up now waits for the restored sign-in first (`auth.ts` `ensureSignedIn`):
//
//     const app = initializeApp(config);                                  // firebase/app
//     const auth = getAuth(app);                                          // firebase/auth
//     connectAuthEmulator(auth, `http://…`);                              // firebase/auth — LOCAL RUNS ONLY
//     await auth.authStateReady();                                        // firebase/auth
//     if (auth.currentUser === null) await signInAnonymously(auth);       // firebase/auth — ONLY when nobody is
//     const check = initializeAppCheck(app, {                             // firebase/app-check — needs a site key
//       provider: new ReCaptchaEnterpriseProvider(config.recaptchaEnterpriseSiteKey),
//       isTokenAutoRefreshEnabled: true,
//     });
//
// Everything around it is what this file already was, and it is unchanged: the config gate, the named refusal, the
// token plumbing and `requireToken`'s never-a-blank rule.

import type { AuthBackend } from "./auth";

/**
 * The Firebase web app's configuration. FIVE FIELDS, ALL SOMEONE ELSE'S.
 *
 * Deliberately not the whole console snippet: `storageBucket` and `messagingSenderId` are for services this client
 * does not use, and a field nobody needs is a field somebody has to go and find before the app can start.
 */
export interface FirebaseWebConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly appId: string;
  /** The reCAPTCHA Enterprise site key App Check uses on web. Public by design; still a console's, not ours. */
  readonly recaptchaEnterpriseSiteKey: string;
}

/** Which build variable carries which field. ONE WRITER for the names, read by the module and by `.env.example`. */
export const FIREBASE_ENV_VARS = {
  apiKey: "VITE_FIREBASE_API_KEY",
  authDomain: "VITE_FIREBASE_AUTH_DOMAIN",
  projectId: "VITE_FIREBASE_PROJECT_ID",
  appId: "VITE_FIREBASE_APP_ID",
  recaptchaEnterpriseSiteKey: "VITE_RECAPTCHA_ENTERPRISE_SITE_KEY",
} as const satisfies { readonly [K in keyof FirebaseWebConfig]: string };

// THE EMULATOR SWITCH AND THE NO-KEY WORD (W4.2-local), held here since B8 beside the five variables they qualify:
// `services/firebaseSdk.ts` starts the SDK with them, and `web/deployTargets.ts` (the build's config, in Node) refuses a
// production build that carries either. Pure, like the rest of this file.

/** Which build variable points the Auth SDK at a local emulator. Unset everywhere; supplied by the emulator, never
 *  by Tyler and never by a console. Its value is a `host:port`, which is the shape the Auth emulator prints on boot. */
export const AUTH_EMULATOR_HOST_VAR = "VITE_FIREBASE_AUTH_EMULATOR_HOST";

/** The one value of `VITE_RECAPTCHA_ENTERPRISE_SITE_KEY` that is not a site key. App Check has no emulator, so a run
 *  against one has to be able to SAY it has no key rather than carry a fake one that would fail at attestation time
 *  with a vendor's error message. */
export const NO_SITE_KEY = "emulator";


/** Whether this build carries a real reCAPTCHA Enterprise site key. Blank counts as absent — `readFirebaseConfig`
 *  already refuses a blank, so reaching here with one would be a hand-built config. */
export function hasRealSiteKey(siteKey: string): boolean {
  const value = siteKey.trim();
  return value !== "" && value !== NO_SITE_KEY;
}

/** The Auth emulator's host, or null. Blank is null: an empty variable is the environment saying nothing. */
export function readAuthEmulatorHost(env: Record<string, string | undefined>): string | null {
  const value = (env[AUTH_EMULATOR_HOST_VAR] ?? "").trim();
  return value === "" ? null : value;
}

/** The two token readers `HttpTransport` needs, and nothing else. */
export interface FirebaseTokens {
  getIdToken(): Promise<string>;
  getAppCheckToken(): Promise<string>;
}

/**
 * THE SIGNED-IN USER ITSELF (WEB-PAY-2, F-WP-2; ACC-2). Until ACC-2 this was three calls — the uid, delete, sign out —
 * for ending the user after the account is deleted, as iOS does. Under one account (ACC-2) it is the whole sign-in port,
 * `AuthBackend` (`services/auth.ts`): one Firebase call per method, implemented by `firebaseSdk.ts`
 * `firebaseAuthBackend`, and driven by the one session both the real client and the fake use.
 */
export type FirebaseAccount = AuthBackend;

/** What starting the SDK hands back: the two token readers, and the account they read for. */
export interface FirebaseStarted extends FirebaseTokens {
  readonly account: FirebaseAccount;
}

/** The port the staging batch implements with the Firebase web SDK. One call, because this client holds a Firebase
 *  app handle for no other purpose. */
export interface FirebaseWebSdk {
  /** Initialises the app, waits for the restored sign-in and signs in anonymously only if nobody is (F-AC-1), starts App
   *  Check, and hands back the two readers and the account. */
  start(config: FirebaseWebConfig): Promise<FirebaseStarted>;
}

/** Why the seam could not produce tokens. Each names the thing that is missing, never just "unavailable". */
export type FirebaseRefusal =
  /** One or more build variables carry no value. `missing` names them, and it is what the app prints. */
  | { readonly kind: "configIncomplete"; readonly missing: readonly string[] }
  /** The config is complete and no SDK was supplied. `main.tsx` always supplies one; a caller that passes none — a
   *  test, or a build that deliberately withholds it — is told so rather than left with a half-built seam. */
  | { readonly kind: "sdkNotBuilt"; readonly problem: string }
  /** The SDK was supplied and failed to start (sign-in refused, App Check could not attest). */
  | { readonly kind: "startFailed"; readonly problem: string };

export type FirebaseSeam =
  | { readonly ok: true; readonly tokens: FirebaseTokens; readonly account: FirebaseAccount; readonly projectId: string }
  | { readonly ok: false; readonly reason: FirebaseRefusal };

/** One sentence naming what is missing, for a log line or a dev banner. Never owner-facing copy: a refusal an owner
 *  reads is the server's sentence, and no server was reached here. */
export function describeFirebaseRefusal(reason: FirebaseRefusal): string {
  switch (reason.kind) {
    case "configIncomplete":
      return `Firebase is not configured: ${reason.missing.join(", ")} ${reason.missing.length === 1 ? "is" : "are"} not set.`;
    case "sdkNotBuilt":
      return `Firebase is configured but this build has no SDK to start it with: ${reason.problem}`;
    case "startFailed":
      return `Firebase could not start: ${reason.problem}`;
  }
}

export type FirebaseConfigResult =
  | { readonly ok: true; readonly config: FirebaseWebConfig }
  | { readonly ok: false; readonly missing: readonly string[] };

/**
 * Reads the five variables. A MISSING FIELD IS NAMED, and ALL the missing ones are named at once — a gate that
 * reported them one at a time would take five runs to tell someone what to go and get.
 */
export function readFirebaseConfig(env: Record<string, string | undefined>): FirebaseConfigResult {
  const entries = Object.entries(FIREBASE_ENV_VARS) as [keyof FirebaseWebConfig, string][];
  const missing = entries.filter(([, name]) => (env[name] ?? "").trim() === "").map(([, name]) => name);
  if (missing.length > 0) return { ok: false, missing };
  const config = Object.fromEntries(
    entries.map(([field, name]) => [field, (env[name] as string).trim()]),
  ) as unknown as FirebaseWebConfig;
  return { ok: true, config };
}

/**
 * A token reader that NEVER hands back a token it did not get.
 *
 * THIS IS THE WHOLE POINT OF THE SEAM AND IT IS EASY TO GET WRONG IN THE COMFORTABLE DIRECTION. A getter that caught
 * its own error and answered `""` would send `Authorization: Bearer ` — an unauthenticated call, answered 401 by a
 * server that has no idea anything went wrong on this end, and indistinguishable from a real sign-in problem. So a
 * failure THROWS, and `contract/v2-client/index.ts`'s `send()` turns a throw from either getter into
 * `{ ok: false, kind: "token", which, problem }` — a typed failure the screen renders as a token failure, by name.
 *
 * An empty string is treated as a failure for the same reason: a provider that returns one has not authenticated
 * anybody, and passing it on would be the silent unauthenticated call by a different route.
 */
export function requireToken(which: "idToken" | "appCheck", read: () => Promise<string>): () => Promise<string> {
  return async (): Promise<string> => {
    const token = await read(); // A throw here propagates, deliberately. It must never be caught and papered over.
    if (typeof token !== "string" || token.trim() === "") {
      throw new Error(`the ${which} provider returned no token`);
    }
    return token;
  };
}

/**
 * THE SEAM. Reads the config, starts the SDK if there is one, and returns tokens or a NAMED reason.
 *
 * With no `sdk` it always refuses. `main.tsx` supplies `createFirebaseWebSdk` (W4.2-local), so what decides a real
 * build is now the CONFIG alone — and with none, the app runs on `FakeTransport` exactly as it did before this module
 * existed. The order matters: a build with neither is told which values it is missing rather than that it has no SDK,
 * because the values are the thing somebody has to go and get.
 */
export async function startFirebase(
  env: Record<string, string | undefined>,
  sdk: FirebaseWebSdk | null = null,
): Promise<FirebaseSeam> {
  const config = readFirebaseConfig(env);
  if (!config.ok) return { ok: false, reason: { kind: "configIncomplete", missing: config.missing } };
  if (sdk === null) {
    return {
      ok: false,
      reason: {
        kind: "sdkNotBuilt",
        problem: "no FirebaseWebSdk was passed to startFirebase; `createFirebaseWebSdk` in services/firebaseSdk.ts "
          + "is the one this app supplies, and it is the only file here that imports `firebase`",
      },
    };
  }
  let tokens: FirebaseStarted;
  try {
    tokens = await sdk.start(config.config);
  } catch (error) {
    return {
      ok: false,
      reason: { kind: "startFailed", problem: error instanceof Error ? error.message : String(error) },
    };
  }
  return {
    ok: true,
    projectId: config.config.projectId,
    tokens: {
      getIdToken: requireToken("idToken", () => tokens.getIdToken()),
      getAppCheckToken: requireToken("appCheck", () => tokens.getAppCheckToken()),
    },
    account: tokens.account,
  };
}
